import { useState } from "react";
import {
  useExcludeStoreV1StoresPlatformStoreIdExcludePost,
  useReadmitStoreV1StoresPlatformStoreIdReadmitPost,
} from "../api/generated/qcsControlPlane";
import type { Platform } from "../api/labels";
import { ago, fmtDateTime } from "../api/labels";
import { useCurrentUser } from "../auth/me";
import { useToast } from "./toast";
import { Button, Dot, Input, Panel, Row, Stack } from "@akaike/ui";

// Data exclusion for one dark store: state, reason, and the control to change it.
//
// Exclusion is orthogonal to the store's lifecycle `status` (open/closed, the merchant
// sweep's verdict) - it says we do not believe this store's readings, so it leaves every
// run scope and every availability/price rollup. That is a deliberate operator decision,
// not a fault, so it is drawn muted rather than on the red/amber semaphore: an excluded
// store is set aside, not broken. The one thing it must never look like is an alarm.
//
// Superuser-only, matching the endpoint. A member sees the state (it changes what every
// number on the page means, so hiding it would be worse than useless) but no control.
export function StoreExclusion({
  platform,
  storeId,
  isExcluded,
  reason,
  excludedAt,
  onChanged,
}: {
  platform: Platform;
  storeId: string;
  isExcluded: boolean;
  reason: string;
  excludedAt: string;
  onChanged: () => void;
}) {
  const me = useCurrentUser();
  const notify = useToast();
  const [dialog, setDialog] = useState(false);

  const exclude = useExcludeStoreV1StoresPlatformStoreIdExcludePost({
    mutation: {
      // Closed on SUCCESS only. On a 409 or a 500 the dialog stays up with the typed
      // reason intact, so a failed attempt costs a retry rather than rewriting it.
      onSuccess: () => {
        setDialog(false);
        notify(`Store ${storeId} excluded from reporting`);
        onChanged();
      },
      onError: (e) => notify(errText(e, "Could not exclude the store"), "crit"),
    },
  });
  const readmit = useReadmitStoreV1StoresPlatformStoreIdReadmitPost({
    mutation: {
      onSuccess: () => {
        notify(`Store ${storeId} readmitted`);
        onChanged();
      },
      onError: (e) => notify(errText(e, "Could not readmit the store"), "crit"),
    },
  });
  const busy = exclude.isPending || readmit.isPending;

  // Nothing to say and nothing to do: a member looking at an ordinary store.
  if (!isExcluded && !me.is_superuser) return null;

  return (
    <>
      <Panel className="mb-3.5">
        <Panel.Head title="Data exclusion">
          <div className="flex-1" />
          <span className="faint text-[11px]">superuser-only</span>
        </Panel.Head>
        <Panel.Body>
          <Row gap="lg" align="center" wrap>
            <Stack gap="xs" style={{ minWidth: 0 }}>
              <Row gap="sm" align="center">
                <Dot tone={isExcluded ? "idle" : "ok"} />
                <span className="strong" style={{ fontSize: 13 }}>
                  {isExcluded ? "Excluded from reporting" : "Counted in all reporting"}
                </span>
              </Row>
              <div className="faint" style={{ fontSize: 11.5, lineHeight: 1.55, maxWidth: 640 }}>
                {isExcluded ? (
                  <>
                    This store is out of every run scope and every availability, price and
                    carriage rollup: including past windows. It is still swept, so its
                    lifecycle keeps updating.
                  </>
                ) : (
                  <>
                    Exclude a store whose readings cannot be believed, a merchant answering
                    from a catalog default rather than from stock, a test location, known-bad
                    routing. Reversible, and the store keeps being swept either way.
                  </>
                )}
              </div>
              {isExcluded && reason && (
                <div className="mt-1.5" style={{ fontSize: 12.5, lineHeight: 1.5, maxWidth: 640 }}>
                  <span className="hint">reason</span> {reason}
                </div>
              )}
              {isExcluded && excludedAt && !isEpoch(excludedAt) && (
                <div className="faint mono" style={{ fontSize: 11 }}>
                  since {fmtDateTime(excludedAt)} · {ago(excludedAt)}
                </div>
              )}
            </Stack>
            <div className="flex-1" />
            {me.is_superuser &&
              (isExcluded ? (
                <Button
                  disabled={busy}
                  loading={readmit.isPending}
                  onClick={() =>
                    readmit.mutate({
                      platform,
                      storeId: encodeURIComponent(storeId),
                    })
                  }
                >
                  {readmit.isPending ? "Readmitting…" : "Readmit store"}
                </Button>
              ) : (
                <Button variant="destructive" disabled={busy} onClick={() => setDialog(true)}>
                  Exclude store
                </Button>
              ))}
          </Row>
        </Panel.Body>
      </Panel>

      {dialog && (
        <ExcludeDialog
          storeId={storeId}
          busy={exclude.isPending}
          onClose={() => setDialog(false)}
          onConfirm={(why) =>
            exclude.mutate({
              platform,
              storeId: encodeURIComponent(storeId),
              data: { platform, reason: why },
            })
          }
        />
      )}
    </>
  );
}

// The reason is the whole point of the confirmation, not a formality: an exclusion nobody
// can justify months later is one nobody can safely undo. So the dialog asks for it
// instead of asking "are you sure", and the confirm stays disabled until it is written.
function ExcludeDialog({
  storeId,
  busy,
  onConfirm,
  onClose,
}: {
  storeId: string;
  busy: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const [why, setWhy] = useState("");
  const ready = why.trim().length > 0;
  return (
    <div className="modal-scrim" onClick={busy ? undefined : onClose}>
      <Panel style={{ width: "100%", maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
        <Panel.Head title={<>Exclude store <span className="mono">{storeId}</span></>}>
          <div className="flex-1" />
          <Button variant="outline" size="sm" icon onClick={onClose} disabled={busy} aria-label="close">
            ✕
          </Button>
        </Panel.Head>
        <Panel.Body>
          <Stack gap="lg">
            <div className="faint" style={{ fontSize: 12.5, lineHeight: 1.55 }}>
              This store leaves every run scope and every availability, price and carriage
              rollup, in past windows as well as future ones. It keeps being swept, and you
              can readmit it here at any time.
            </div>
            <label className="field">
              <span className="hint">Why is this store not believable?</span>
              <Input
                value={why}
                onChange={(e) => setWhy(e.target.value)}
                placeholder="e.g. phantom merchant: qty=1 for every SKU"
                maxLength={500}
                autoFocus
                disabled={busy}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && ready && !busy) onConfirm(why.trim());
                }}
              />
            </label>
            <Row gap="sm" justify="end">
              <Button variant="outline" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => onConfirm(why.trim())}
                disabled={!ready || busy}
                loading={busy}
              >
                {busy ? "Excluding…" : "Exclude store"}
              </Button>
            </Row>
          </Stack>
        </Panel.Body>
      </Panel>
    </div>
  );
}

// `excluded_at` is an epoch sentinel when the store was never excluded (the column is
// only meaningful alongside the flag), and rendering 1970 would read as a real date.
function isEpoch(ts: string): boolean {
  return ts.startsWith("1970-01-01");
}

function errText(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}
